import React, { useState } from 'react';
import { slugify, PlusIconPath } from './constants';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import ImageUpload, { resolveImageUrl } from './ImageUpload';

interface Props {
  data: any;
  onChange: (data: any) => void;
  websiteId?: string;
  domain?: string;
}

const TrashIcon: React.FC<{ className?: string }> = ({ className = 'w-4 h-4' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" />
  </svg>
);

const WrenchIcon: React.FC<{ className?: string }> = ({ className = 'w-10 h-10' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M11.42 15.17 17.25 21A2.652 2.652 0 0 0 21 17.25l-5.877-5.877M11.42 15.17l2.496-3.03c.317-.384.74-.626 1.208-.766M11.42 15.17l-4.655 5.653a2.548 2.548 0 1 1-3.586-3.586l6.837-5.63m5.108-.233c.55-.164 1.163-.188 1.743-.14a4.5 4.5 0 0 0 4.486-6.336l-3.276 3.277a3.004 3.004 0 0 1-2.25-2.25l3.276-3.276a4.5 4.5 0 0 0-6.336 4.486c.049.58.025 1.193-.14 1.743" />
  </svg>
);

const ServicesEditor: React.FC<Props> = ({ data, onChange, websiteId, domain }) => {
  const siteBaseUrl = domain ? `https://${domain}` : undefined;
  const form = data || { services: [] };
  const [expandedIdx, setExpandedIdx] = useState<number | null>(null);

  const emit = (newForm: any) => onChange(newForm);

  const updateService = (idx: number, field: string, value: any) => {
    const services = [...form.services];
    services[idx] = { ...services[idx], [field]: value };
    if (field === 'name') {
      services[idx].slug = slugify(value);
    }
    emit({ ...form, services });
  };

  const updateFeature = (svcIdx: number, featIdx: number, value: string) => {
    const services = [...form.services];
    const features = [...(services[svcIdx].features || [])];
    features[featIdx] = value;
    services[svcIdx] = { ...services[svcIdx], features };
    emit({ ...form, services });
  };

  const addFeature = (svcIdx: number) => {
    const services = [...form.services];
    services[svcIdx] = { ...services[svcIdx], features: [...(services[svcIdx].features || []), ''] };
    emit({ ...form, services });
  };

  const removeFeature = (svcIdx: number, featIdx: number) => {
    const services = [...form.services];
    const features = [...(services[svcIdx].features || [])];
    features.splice(featIdx, 1);
    services[svcIdx] = { ...services[svcIdx], features };
    emit({ ...form, services });
  };

  const addService = () => {
    const services = [...(form.services || []), {
      slug: 'new-service',
      name: 'New Service',
      shortDescription: '',
      longDescription: '',
      features: [],
    }];
    emit({ ...form, services });
    setExpandedIdx(services.length - 1);
  };

  const removeService = (idx: number) => {
    const services = [...form.services];
    services.splice(idx, 1);
    emit({ ...form, services });
    if (expandedIdx === idx) setExpandedIdx(null);
    else if (expandedIdx !== null && expandedIdx > idx) setExpandedIdx(expandedIdx - 1);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h2 className="text-lg font-display font-semibold text-primary">Services</h2>
          <p className="text-sm text-muted mt-1">{form.services?.length || 0} services (6-8 required)</p>
        </div>
        <button onClick={addService} className="inline-flex items-center gap-2 px-4 py-2 bg-accent/10 text-accent rounded-lg text-sm font-medium hover:bg-accent/20 transition-colors">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d={PlusIconPath} />
          </svg>
          Add Service
        </button>
      </div>

      {(!form.services || form.services.length === 0) && (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <WrenchIcon className="w-10 h-10 text-zinc-300 mb-3" />
          <p className="text-sm text-muted mb-4">No services yet</p>
          <button onClick={addService} className="px-4 py-2 bg-accent text-white rounded-lg text-sm font-medium hover:bg-accent/90 transition-colors">
            Add your first service
          </button>
        </div>
      )}

      {(form.services || []).map((svc: any, idx: number) => (
        <div
          key={idx}
          data-editor-key={`services.${idx}`}
          data-editor-expanded={expandedIdx === idx ? 'true' : 'false'}
          tabIndex={-1}
          className="border border-zinc-200 rounded-lg overflow-hidden bg-white focus:outline-none focus:ring-2 focus:ring-primary"
        >
          <button
            onClick={() => setExpandedIdx(expandedIdx === idx ? null : idx)}
            className="w-full px-4 py-3 flex items-center justify-between bg-zinc-50 hover:bg-zinc-100 transition-colors text-left"
          >
            <div className="flex items-center gap-3">
              {svc.image ? (
                <img
                  src={resolveImageUrl(svc.image, siteBaseUrl)}
                  alt=""
                  className="w-10 h-10 rounded object-cover shrink-0"
                  onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                />
              ) : (
                <div className="w-10 h-10 rounded bg-zinc-200 shrink-0" />
              )}
              <div className="flex items-center gap-2 min-w-0">
                <span className="font-medium text-primary truncate">{svc.name || `Service ${idx + 1}`}</span>
                {svc.slug && <span className="text-xs text-muted font-mono shrink-0 hidden sm:inline">/{svc.slug}</span>}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span
                role="button"
                onClick={(e) => { e.stopPropagation(); removeService(idx); }}
                className="p-1 text-zinc-400 hover:text-red-500 transition-colors"
                title="Remove service"
              >
                <TrashIcon />
              </span>
              <svg className={`w-4 h-4 text-muted transition-transform ${expandedIdx === idx ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5" />
              </svg>
            </div>
          </button>

          {expandedIdx === idx && (
            <div className="p-4 space-y-4">
              {websiteId && (
                <div className="space-y-1.5">
                  <Label>Service Image</Label>
                  <ImageUpload
                    websiteId={websiteId}
                    currentUrl={svc.image || undefined}
                    path="services"
                    onUploaded={(url) => updateService(idx, 'image', url)}
                    onRemoved={() => updateService(idx, 'image', '')}
                    siteBaseUrl={siteBaseUrl}
                    aspectRatio="aspect-[3/1]"
                  />
                </div>
              )}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label>Service Name</Label>
                  <Input value={svc.name || ''} onChange={(e) => updateService(idx, 'name', e.target.value)} />
                  <p className="text-xs text-muted mt-1">Page URL: <span className="font-mono">/{svc.slug || '—'}</span></p>
                </div>
                <div className="space-y-1.5">
                  <Label>Icon</Label>
                  <Input value={svc.icon || ''} onChange={(e) => updateService(idx, 'icon', e.target.value)} placeholder="e.g. wrench" />
                  <p className="text-xs text-muted">Icon name shown next to the service card</p>
                </div>
              </div>

              <div className="space-y-1.5">
                <Label>Short description <span className="font-normal text-muted text-xs">(shown on homepage cards)</span></Label>
                <Input value={svc.shortDescription || ''} onChange={(e) => updateService(idx, 'shortDescription', e.target.value)} maxLength={160} placeholder="One sentence about this service" />
                <span className="text-xs text-muted">{(svc.shortDescription || '').length}/160 characters</span>
              </div>

              <div className="space-y-1.5">
                <Label>Full description <span className="font-normal text-muted text-xs">(shown on the service's own page)</span></Label>
                <textarea rows={3} value={svc.longDescription || ''} onChange={(e) => updateService(idx, 'longDescription', e.target.value)}
                  placeholder="A few sentences describing what this service includes, why customers need it, etc."
                  className="flex min-h-[80px] w-full rounded-md border border-zinc-200 bg-zinc-100/70 px-3 py-2 text-sm text-zinc-900 placeholder:text-zinc-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/30 focus-visible:border-accent transition-colors" />
              </div>

              <div>
                <Label className="mb-1.5 block">Bullet points / key features <span className="font-normal text-muted text-xs">(2–8)</span></Label>
                <p className="text-xs text-muted mb-2">Short phrases listed on the service page — e.g. "Licensed &amp; insured", "Same-day appointments"</p>
                {(svc.features || []).map((feat: string, fi: number) => (
                  <div key={fi} className="flex gap-2 mb-2">
                    <Input value={feat} onChange={(e) => updateFeature(idx, fi, e.target.value)} className="flex-1" />
                    <button onClick={() => removeFeature(idx, fi)} className="px-2 text-red-500 hover:text-red-700">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" /></svg>
                    </button>
                  </div>
                ))}
                {(svc.features || []).length < 8 && (
                  <button onClick={() => addFeature(idx)} className="inline-flex items-center gap-1 text-sm text-accent hover:text-accent/80">
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d={PlusIconPath} />
                    </svg>
                    Add feature
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default ServicesEditor;
